import { MdsIconKind } from "@/design-system/components/icon";
import { ItemPreviewState } from "@/design-system/components/item-list/rows/item-preview/ItemPreviewState";
import { MdsItemListRowFeaturedContent } from "@/design-system/components/item-list/rows/MdsItemListRowFeaturedContent";
import {
  MdsItemListItemRowData,
  MdsItemListRowType,
} from "@/design-system/components/item-list/types/MdsItemListRowTypes";
import { MdsItemKind } from "@/design-system/components/item-list/types/MdsItemTypes";
import { MdsItemDropdown } from "@/design-system/components/item-list/types/SharedTypes";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { generateShortDateString } from "@/domains/date/date";
import { MDSRowItemSize } from "@/mds";
import { AppStore } from "@/store/AppStore";
import { INoteObservable } from "@/store/note";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { DateTime } from "luxon";

export const getRowForNoteObservable = ({
  dateTime,
  dropdown,
  inMainPanel,
  listState,
  noteObservable,
  store,
  trackClick,
  tabId,
  disablePreview = false,
}: {
  dateTime: Date | string;
  dropdown?: MdsItemDropdown;
  inMainPanel: boolean;
  listState?: ListStateObservable;
  noteObservable: INoteObservable;
  store: AppStore;
  trackClick?: () => void;
  tabId?: string;
  disablePreview?: boolean;
}) => {
  const noteId = noteObservable.id;
  const dateLabel = generateShortDateString(
    typeof dateTime === "string" ? DateTime.fromISO(dateTime) : DateTime.fromJSDate(dateTime)
  );

  const isLoading = noteObservable.collectionList?.isLoading;
  const collections = noteObservable.collectionList?.allCollections || [];

  const suggestionData = noteObservable.suggestionData;
  const primaryLabel = suggestionData?.suggested_primary_label || noteObservable.title;
  const secondaryLabel =
    suggestionData?.suggested_secondary_label ||
    noteObservable.secondaryTitle ||
    "No additional text";

  const row: MdsItemListItemRowData = {
    type: MdsItemListRowType.Item,
    key: noteObservable.id,
    size: MDSRowItemSize.XLarge,
    payload: {
      id: noteObservable.id,
      kind:
        noteObservable.id === store.audioCapture.activeNoteId
          ? MdsItemKind.ActiveAudioCaptureNote
          : MdsItemKind.Note,
      titleIcon: noteObservable.isShared ? { kind: MdsIconKind.Shared } : undefined,
      createPreviewState: disablePreview
        ? undefined
        : () =>
            new ItemPreviewState({
              store,
              id: noteId,
              kind: MdsItemKind.Note,
            }),
      label: primaryLabel,
      onClick: ({ event }) => {
        store.navigation.goToNote({ noteId, event, activeTabId: tabId });
        trackClick?.();
      },
      sharedBy: noteObservable.sharedBy,
      dateLabel,
      dropdown,
      listState,
      chips: () => {
        const sortedCollections = sortCollectionsForChips(collections);
        if (sortedCollections.length === 0) return null;
        return <MdsItemListRowFeaturedContent inline collections={sortedCollections} />;
      },
      extraRow: {
        id: `note-${noteId}-content`,
        content: () => (
          <MdsItemListRowFeaturedContent
            snippet={[{ text: secondaryLabel }]}
            isLoading={isLoading}
          />
        ),
      },
    },
  };
  if (!inMainPanel) {
    row.payload.extraRow = {
      id: `note-${noteId}-content`,
      content: () => <MdsItemListRowFeaturedContent snippet={[{ text: dateLabel }]} />,
    };
    row.payload.onClick = () => {
      store.navigation.goToNote({ noteId });
      trackClick?.();
    };
    row.payload.sharedBy = undefined;
    row.payload.dateLabel = undefined;
    row.payload.dropdown = undefined;
    row.payload.listState = undefined;
  }
  return row;
};
